import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { statsAPI, kpisAPI, leadsAPI, eventsAPI } from "../../lib/api";
import {
    Users, GitBranch, Star, BookOpen, BarChart3, TrendingUp,
    MessageCircle, Heart, Clock, ShoppingBag, AlertTriangle,
    ArrowUpRight, Mic2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const SEGMENTO_LABELS = {
    geral: "Geral", mulheres_com_proposito: "Mulheres c/ Propósito",
    mesa_de_ester: "Mesa de Ester", luto_ao_recomeco: "Do Luto ao Recomeço",
    devocional: "Devocional", musica: "Música", livro: "Livro",
};

const SEGMENTO_COLORS = {
    geral: "bg-stone-700", mulheres_com_proposito: "bg-amber-700",
    mesa_de_ester: "bg-rose-800", luto_ao_recomeco: "bg-indigo-700",
    devocional: "bg-sky-700", musica: "bg-violet-700", livro: "bg-emerald-700",
};

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
}

function StatCard({ icon: Icon, label, value, sub, color = "text-amber-400", href, trend }) {
    const content = (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 hover:border-stone-700 transition-all group">
            <div className="flex items-start justify-between">
                <div className={`p-2 rounded-lg bg-stone-800 ${color}`}>
                    <Icon className="w-5 h-5" />
                </div>
                {href && <ArrowUpRight className="w-4 h-4 text-stone-600 group-hover:text-stone-400 transition-colors" />}
            </div>
            <div className="mt-3">
                <p className="text-2xl font-bold text-stone-100">{value ?? "—"}</p>
                <p className="text-xs text-stone-400 mt-0.5">{label}</p>
                {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
            </div>
        </div>
    );
    return href ? <Link to={href}>{content}</Link> : content;
}

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [kpis, setKpis] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            statsAPI.dashboard(),
            kpisAPI.latest(),
            eventsAPI.list(),
        ])
            .then(([s, k, ev]) => {
                setStats(s.data);
                setKpis(k.data || []);
                setEvents((ev.data || []).slice(0, 5));
            })
            .catch(() => toast.error("Erro ao carregar dashboard"))
            .finally(() => setLoading(false));
    }, []);

    const kpiMap = {};
    kpis.forEach(k => { kpiMap[k._id?.metric] = k; });

    const leadsBySeg = stats?.leads?.by_segmento || [];

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-100">
                        {greeting()}, {user?.name?.split(" ")[0]} 👋
                    </h1>
                    <p className="text-stone-400 text-sm mt-0.5">
                        {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
                    </p>
                </div>
                <div className="flex items-center gap-2 bg-stone-900 border border-stone-800 rounded-xl px-4 py-2">
                    <Mic2 className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-stone-300 font-medium">Tayane Larini · Hub</span>
                </div>
            </div>

            {/* Stats principais */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total de Leads" value={stats?.leads?.total}
                    sub={`${stats?.leads?.novos || 0} novos`} href="/leads" color="text-amber-400" />
                <StatCard icon={Heart} label="Convertidos" value={stats?.leads?.convertidos}
                    color="text-rose-400" href="/leads" />
                <StatCard icon={Star} label="Eventos Ativos" value={stats?.events?.total}
                    href="/eventos" color="text-violet-400" />
                <StatCard icon={Clock} label="Lista de Espera" value={stats?.waitlist?.aguardando}
                    href="/waitlist" color="text-sky-400" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={ShoppingBag} label="Vendas Confirmadas" value={stats?.orders?.confirmados}
                    href="/financeiro" color="text-emerald-400" />
                <StatCard icon={BookOpen} label="Alertas de Estoque" value={stats?.stock_alerts}
                    sub={stats?.stock_alerts > 0 ? "Reposição necessária" : "Estoque OK"}
                    href="/estoque" color={stats?.stock_alerts > 0 ? "text-red-400" : "text-emerald-400"} />
                <StatCard icon={MessageCircle} label="Menções (7 dias)" value={stats?.mentions_week}
                    href="/mencoes" color="text-indigo-400" />
                <StatCard icon={GitBranch} label="Fluxos Ativos" value="—"
                    href="/flows" color="text-amber-400" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Leads por segmento */}
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-semibold text-stone-200">Leads por Jornada</h2>
                        <Link to="/leads" className="text-xs text-amber-500 hover:text-amber-400">Ver todos →</Link>
                    </div>
                    <div className="space-y-2">
                        {leadsBySeg.length === 0 && (
                            <p className="text-xs text-stone-500 py-4 text-center">Nenhum lead ainda</p>
                        )}
                        {leadsBySeg.sort((a,b) => b.count - a.count).map(seg => {
                            const total = stats?.leads?.total || 1;
                            const pct = Math.round((seg.count / total) * 100);
                            return (
                                <div key={seg._id}>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-stone-300">{SEGMENTO_LABELS[seg._id] || seg._id}</span>
                                        <span className="text-xs text-stone-400">{seg.count}</span>
                                    </div>
                                    <div className="h-1.5 bg-stone-800 rounded-full">
                                        <div className={`h-1.5 rounded-full ${SEGMENTO_COLORS[seg._id] || "bg-stone-600"}`}
                                            style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* KPIs externos */}
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-semibold text-stone-200">KPIs Externos</h2>
                        <Link to="/analytics" className="text-xs text-amber-500 hover:text-amber-400">Painel →</Link>
                    </div>
                    {kpis.length === 0 ? (
                        <div className="text-center py-6">
                            <BarChart3 className="w-8 h-8 text-stone-700 mx-auto mb-2" />
                            <p className="text-xs text-stone-500">Aguardando dados do n8n</p>
                            <p className="text-xs text-stone-600 mt-1">Configure o n8n para coletar KPIs</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {kpis.slice(0, 5).map((k, i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <span className="text-xs text-stone-400">{k.label || k._id?.metric}</span>
                                    <span className="text-sm font-semibold text-stone-100">
                                        {Number(k.value).toLocaleString("pt-BR")}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Próximos eventos */}
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-semibold text-stone-200">Próximos Eventos</h2>
                        <Link to="/eventos" className="text-xs text-amber-500 hover:text-amber-400">Ver todos →</Link>
                    </div>
                    {events.length === 0 ? (
                        <div className="text-center py-6">
                            <Star className="w-8 h-8 text-stone-700 mx-auto mb-2" />
                            <p className="text-xs text-stone-500">Nenhum evento publicado</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {events.map(ev => (
                                <div key={ev.id} className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center flex-shrink-0">
                                        <Star className="w-3.5 h-3.5 text-amber-500" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium text-stone-200 truncate">{ev.title}</p>
                                        <p className="text-xs text-stone-500">
                                            {ev.date ? new Date(ev.date).toLocaleDateString("pt-BR") : "—"}
                                            {ev.participants_count > 0 && ` · ${ev.participants_count} inscritos`}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Alertas */}
            {stats?.stock_alerts > 0 && (
                <div className="bg-red-950/40 border border-red-800/50 rounded-xl p-4 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium text-red-300">
                            {stats.stock_alerts} produto(s) com estoque baixo
                        </p>
                        <p className="text-xs text-red-400/70 mt-0.5">
                            Verifique o módulo de estoque e faça a reposição antes do próximo pedido.
                        </p>
                    </div>
                    <Link to="/estoque" className="ml-auto text-xs text-red-300 hover:text-red-200 whitespace-nowrap">
                        Ver estoque →
                    </Link>
                </div>
            )}

            {/* Atalhos rápidos */}
            <div>
                <h2 className="text-sm font-semibold text-stone-400 mb-3">Atalhos rápidos</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { label: "Novo Lead", icon: Users, href: "/leads", color: "text-amber-400" },
                        { label: "Novo Fluxo", icon: GitBranch, href: "/flows/novo", color: "text-indigo-400" },
                        { label: "Novo Evento", icon: Star, href: "/eventos", color: "text-violet-400" },
                        { label: "Gerar Roteiro", icon: Sparkles, href: "/roteiros", color: "text-sky-400" },
                    ].map(item => (
                        <Link key={item.href} to={item.href}
                            className="bg-stone-900 border border-stone-800 rounded-xl p-4 flex items-center gap-3 hover:border-stone-700 hover:bg-stone-800/50 transition-all group">
                            <item.icon className={`w-5 h-5 ${item.color}`} />
                            <span className="text-sm text-stone-300 group-hover:text-stone-100 transition-colors">{item.label}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
